import {
  EditOutlined,
  ExclamationCircleOutlined,
  LineChartOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Switch, Tooltip } from "antd";
import clsx from "clsx";
import React, { useState, useEffect, useRef } from "react";
import { PlayButtonIcon } from "./icons/PlayButtonIcon";
import { PublishButtonIcon } from "./PublishButtonIcon";
import { RedoButtonIcon } from "./icons/RedoButtonIcon";
import {
  saveDraft,
  publish,
  viewDraft,
  tryDraft,
} from "./reducer/actionCreators";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import { UndoButtonIcon } from "./icons/UndoButtonIcon";
import { useNavigate } from "react-router-dom";
import "./ControlRow.css";

export function ControlButton({
  showTitle = true,
  title,
  appearance,
  icon,
  shakeAnimation = false,
  ...props
}: {
  title: string;
  appearance?: string;
  icon?: any; // TODO: This is a component type
  showTitle?: boolean;
  shakeAnimation?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const commonClasses = [
    "border border-black rounded-lg h-10 px-2.5 transition-colors",
    { "opacity-30": props.disabled, "cursor-default": props.disabled },
  ];
  switch (appearance) {
    case "none":
      return (
        <button {...props} className={props.disabled ? "opacity-30" : ""}>
          {icon} {showTitle && title}
        </button>
      );
    case "white":
      return (
        <button
          {...props}
          className={clsx(commonClasses, " bg-white", {
            "hover:bg-gray-100": !props.disabled,
          })}
        >
          {icon} {showTitle && title}
        </button>
      );
    case "orange":
      return (
        <button
          {...props}
          className={clsx(commonClasses, "bg-dots-vibrant-orange text-white", {
            "hover:bg-dots-dark-orange": !props.disabled,
          })}
        >
          {icon} {showTitle && title}
        </button>
      );
    case "black":
      return (
        <button
          {...props}
          className={clsx(
            commonClasses,
            " bg-black text-white flex justify-center items-center gap-1",
            {
              "hover:bg-gray-600": !props.disabled,
            }
          )}
        >
          {icon} {showTitle && title}
        </button>
      );
    case "yellow":
      return (
        <button
          {...props}
          className={clsx(commonClasses, "bg-yellow-200", {
            "hover:bg-yellow-300": !props.disabled,
          })}
        >
          {icon} {title}
        </button>
      );
    default:
      if (icon) {
        return (
          <button
            {...props}
            {...(title === "Close"
              ? { "data-description": "hide-form-button" }
              : null)}
            className={clsx(
              commonClasses,
              "w-10 h-10 bg-black text-white flex justify-center items-center",
              { "hover:bg-gray-600": !props.disabled },
              { "validation-errors-animation": shakeAnimation }
            )}
          >
            {icon}
          </button>
        );
      }
      return (
        <button
          {...props}
          className={clsx(commonClasses, " bg-black text-white", {
            "hover:bg-gray-600": !props.disabled,
          })}
        >
          {title}
        </button>
      );
  }
}

/**
 * The row of control buttons such as Undo, Redo, Edit, Publish etc.
 */
export function ControlRow() {
  const { state, onPersist, dispatch, validationErrors } =
    useTriggerEditorStateDispatch();

  const [saveText, setSaveText] = useState("");
  const [openErrorsOnHover, setOpenErrorsOnHover] = useState(false);

  const isEditing = state.trigger.current.isDraft;
  const isErrorBoxVisible =
    state.editor.visibleErrorBoxId === "validation-errors";

  useEffect(() => {
    if (saveText === "") {
      if (state.editor.isPersisting) setSaveText("Saving...");
    } else {
      if (!state.editor.isPersisting) {
        setSaveText("Saved!");
        setTimeout(() => {
          setSaveText("");
        }, 2000);
      }
    }
  }, [state.editor.isPersisting]);

  const showErrorsOnHover = (open: boolean) => {
    // open validation errors modal on hover, if it isn't open already
    if (!isErrorBoxVisible && Boolean(validationErrors?.length)) {
      dispatch({
        type: "show_errors_box",
        payload: { errorBoxId: "validation-errors" },
      });
      setOpenErrorsOnHover(true);
    }
    // Close modal when mouse leaves publish button: it will only close if was opened by hovering!
    if (!open && isErrorBoxVisible && openErrorsOnHover) {
      dispatch({
        type: "hide_errors_box",
      });
      setOpenErrorsOnHover(false);
    }
  };

  return (
    <div className="absolute top-4 right-0 mr-4 z-20 flex gap-2 items-center">
      {
        <div className="flex gap-2 items-center">
          {isEditing ? (
            <>
              {saveText}
              {Boolean(validationErrors?.length) && (
                <Tooltip
                  placement="bottom"
                  title="Click here to see the errors"
                >
                  <ControlButton
                    title="Validation errors"
                    shakeAnimation
                    icon={
                      <ExclamationCircleOutlined
                        style={{
                          fontSize: "1.2rem",
                          margin: "3px 0 0 0",
                        }}
                      />
                    }
                    onClick={() => {
                      window.analytics.track(
                        "Trigger - validation errors clicked"
                      );
                      dispatch({
                        type: "show_errors_box",
                        payload: { errorBoxId: "validation-errors" },
                      });
                    }}
                  />
                </Tooltip>
              )}
              <Tooltip placement="bottom" title="Settings">
                <ControlButton
                  title="More"
                  icon={
                    <SettingOutlined
                      style={{
                        fontSize: "1.2rem",
                        margin: "3px 0 0 0",
                      }}
                    />
                  }
                  onClick={() => {
                    dispatch({
                      type: "show_modal",
                      payload: { modalId: "settings" },
                    });
                    window.analytics.track("Trigger - settings clicked");
                  }}
                  disabled={Boolean(state.editor.isPersisting)}
                />
              </Tooltip>
              <Tooltip placement="bottom" title="Undo">
                <ControlButton
                  title="Undo"
                  icon={<UndoButtonIcon />}
                  disabled={state.trigger.past.length === 0}
                  onClick={() => {
                    dispatch({ type: "undo" });
                    dispatch(saveDraft(onPersist));
                    window.analytics.track("Trigger - undo");
                  }}
                />
              </Tooltip>
              <Tooltip placement="bottom" title="Redo">
                <ControlButton
                  title="Redo"
                  icon={<RedoButtonIcon />}
                  disabled={state.trigger.future.length === 0}
                  onClick={() => {
                    dispatch({ type: "redo" });
                    dispatch(saveDraft(onPersist));
                    window.analytics.track("Trigger - redo");
                  }}
                />
              </Tooltip>
              <Tooltip
                title={
                  Boolean(validationErrors?.length)
                    ? "Please fix errors before testing"
                    : ""
                }
                placement="bottom"
                mouseLeaveDelay={0}
                overlayStyle={{ width: "160px" }}
                onVisibleChange={showErrorsOnHover}
              >
                <div className="inline-block">
                  <ControlButton
                    title="Test"
                    showTitle={true}
                    icon={<PlayButtonIcon />}
                    appearance="black"
                    onClick={() => {
                      dispatch({
                        type: "show_modal",
                        payload: { modalId: "try-draft" },
                      });
                      dispatch(tryDraft(onPersist));
                      window.analytics.track("Trigger - attempt to test");
                    }}
                    disabled={
                      Boolean(state.editor.isPersisting) ||
                      Boolean(validationErrors?.length)
                    }
                  />
                </div>
              </Tooltip>

              <Tooltip
                title={
                  Boolean(validationErrors?.length)
                    ? "Please fix errors before publishing"
                    : ""
                }
                placement="bottomRight"
                mouseLeaveDelay={0}
                overlayStyle={{ width: "160px" }}
                onVisibleChange={showErrorsOnHover}
              >
                <div className="inline-block">
                  <ControlButton
                    title="Publish"
                    appearance="orange"
                    icon={<PublishButtonIcon />}
                    disabled={
                      Boolean(state.editor.isPersisting) ||
                      Boolean(validationErrors?.length)
                    }
                    onClick={() => {
                      dispatch(publish(onPersist));
                      window.analytics.track("Trigger - attempt to publish");
                    }}
                  />
                </div>
              </Tooltip>
            </>
          ) : (
            <>
              <Switch
                checkedChildren="On"
                unCheckedChildren="Off"
                onChange={() => {}}
                checked={state.trigger.current.isActive}
                disabled={Boolean(state.editor.isPersisting)}
              />
              <ControlButton
                title="Edit"
                appearance="white"
                icon={<EditOutlined />}
                disabled={Boolean(state.editor.isPersisting)}
                onClick={() => {
                  dispatch(viewDraft(onPersist));
                  window.analytics.track(
                    "Trigger - edit button clicked in editor"
                  );
                }}
              />
            </>
          )}
        </div>
      }
    </div>
  );
}
